import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSearch } from '@tanstack/react-router';
import type { CrmInstagramAuthMethodDto } from '@upme/contracts';
import { CrmInstagramAccountsDto, CrmInstagramConnectStartDto } from '@upme/contracts';
import { Button } from '@upme/ui/components/button';
import { useEffect, useState } from 'react';
import { api } from '../../../app/api-client';
import { crmCitiesOptions } from '../queries';

const STATUS_LABEL: Record<string, string> = {
  CONNECTING: 'Подключается',
  ACTIVE: 'Активен',
  DISABLED: 'Отключён',
  ERROR: 'Ошибка',
  REAUTH_REQUIRED: 'Нужна повторное подключение Instagram',
};

const AUTH_METHOD_LABEL: Record<CrmInstagramAuthMethodDto, string> = {
  META_BUSINESS: 'Meta Business',
  INSTAGRAM_LOGIN: 'Instagram Login',
};

export function CrmInstagramConnectPanel() {
  const queryClient = useQueryClient();
  const search = useSearch({ from: '/_authenticated/crm/instagram' }) as {
    connected?: string;
    error?: string;
  };
  const { data: cities } = useQuery(crmCitiesOptions());
  const accountsQuery = useQuery({
    queryKey: ['admin', 'crm', 'instagram', 'accounts'],
    queryFn: () =>
      api
        .get('admin/crm/messaging/instagram/accounts')
        .json()
        .then((d) => CrmInstagramAccountsDto.parse(d)),
  });

  const [label, setLabel] = useState('');
  const [cityIds, setCityIds] = useState<string[]>([]);
  const [banner, setBanner] = useState<string | null>(null);

  useEffect(() => {
    if (search.connected === '1') {
      setBanner('Instagram-аккаунт подключён. Диалоги появятся после входящих сообщений.');
      void queryClient.invalidateQueries({ queryKey: ['admin', 'crm', 'instagram'] });
      void queryClient.invalidateQueries({ queryKey: ['admin', 'crm', 'messaging'] });
    } else if (search.error) {
      setBanner('Не удалось завершить авторизацию. Проверьте разрешения и настройки Meta.');
    }
  }, [search.connected, search.error, queryClient]);

  const start = useMutation({
    mutationFn: (opts: {
      authMethod: CrmInstagramAuthMethodDto;
      reconnectAccountId?: string;
      labelOverride?: string;
      cityIdsOverride?: string[];
    }): Promise<CrmInstagramConnectStartDto> => {
      const effectiveLabel = opts.labelOverride ?? label;
      const effectiveCityIds = opts.cityIdsOverride ?? cityIds;
      return api
        .post('admin/crm/messaging/instagram/connect/start', {
          json: {
            label: effectiveLabel,
            cityIds: effectiveCityIds,
            authMethod: opts.authMethod,
            ...(opts.reconnectAccountId ? { reconnectAccountId: opts.reconnectAccountId } : {}),
          },
        })
        .json()
        .then((d) => CrmInstagramConnectStartDto.parse(d));
    },
    onSuccess: (data) => {
      window.location.href = data.authorizeUrl;
    },
  });

  const disconnect = useMutation({
    mutationFn: (accountId: string) =>
      api.post(`admin/crm/messaging/instagram/accounts/${accountId}/disconnect`),
    onSuccess: () => void accountsQuery.refetch(),
  });

  function toggleCity(id: string) {
    setCityIds((prev) => (prev.includes(id) ? prev.filter((c) => c !== id) : [...prev, id]));
  }

  const canStart = label.trim().length > 0 && cityIds.length > 0 && !start.isPending;

  return (
    <div className="mx-auto max-w-2xl space-y-8 p-6">
      <header>
        <h1 className="text-2xl font-semibold">Instagram Direct</h1>
        <p className="mt-2 text-sm text-muted-foreground">
          Два способа подключения профессиональных аккаунтов. Переписки в CRM общие для всех подключений.
        </p>
      </header>

      {banner ? (
        <p className="rounded-md border border-border bg-muted/40 px-3 py-2 text-sm">{banner}</p>
      ) : null}

      <section className="space-y-3 rounded-lg border p-4">
        <h2 className="font-medium">Подключённые аккаунты</h2>
        {accountsQuery.data?.items.length ? (
          <ul className="space-y-2">
            {accountsQuery.data.items.map((acc) => (
              <li key={acc.id} className="rounded-md border px-3 py-2 text-sm">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div>
                    <p className="font-medium">{acc.label}</p>
                    <p className="text-xs text-muted-foreground">
                      {STATUS_LABEL[acc.status] ?? acc.status}
                      {acc.username ? ` · @${acc.username}` : ''}
                      {` · ${AUTH_METHOD_LABEL[acc.authMethod]}`}
                    </p>
                    {acc.statusDetail ? (
                      <p className="mt-1 text-xs text-amber-700 dark:text-amber-400">{acc.statusDetail}</p>
                    ) : null}
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {acc.status === 'REAUTH_REQUIRED' || acc.status === 'ERROR' ? (
                      <Button
                        type="button"
                        size="sm"
                        variant="secondary"
                        disabled={start.isPending}
                        onClick={() =>
                          start.mutate({
                            authMethod: acc.authMethod,
                            reconnectAccountId: acc.id,
                            labelOverride: acc.label,
                            cityIdsOverride: acc.cityIds,
                          })
                        }
                      >
                        Переподключить
                      </Button>
                    ) : null}
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      disabled={disconnect.isPending}
                      onClick={() => disconnect.mutate(acc.id)}
                    >
                      Отключить
                    </Button>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-sm text-muted-foreground">Пока нет подключённых Instagram-аккаунтов.</p>
        )}
      </section>

      <section className="space-y-4 rounded-lg border p-4">
        <h2 className="font-medium">Добавить аккаунт</h2>
        <label className="block text-sm">
          Название в CRM
          <input
            className="mt-1 w-full rounded-md border border-input px-3 py-2"
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            placeholder="Напр. UpMe Batumi"
          />
        </label>
        <fieldset className="text-sm">
          <legend className="mb-2">Города</legend>
          <div className="flex flex-wrap gap-2">
            {cities?.map((c) => (
              <label key={c.id} className="flex items-center gap-1 rounded border px-2 py-1">
                <input type="checkbox" checked={cityIds.includes(c.id)} onChange={() => toggleCity(c.id)} />
                {c.name}
              </label>
            ))}
          </div>
        </fieldset>

        <div className="grid gap-4 md:grid-cols-2">
          <div className="space-y-2 rounded-md border border-border/80 p-3">
            <h3 className="text-sm font-medium">Вариант 1 — через Meta Business</h3>
            <p className="text-xs text-muted-foreground">
              Подключение Instagram через Facebook и Meta Business. Подходит для аккаунтов в бизнес-портфолио.
            </p>
            <Button
              type="button"
              variant="secondary"
              className="w-full"
              disabled={!canStart}
              onClick={() => start.mutate({ authMethod: 'META_BUSINESS' })}
            >
              Подключить через Meta Business
            </Button>
          </div>
          <div className="space-y-2 rounded-md border border-border/80 p-3">
            <h3 className="text-sm font-medium">Вариант 2 — напрямую через Instagram</h3>
            <p className="text-xs text-muted-foreground">
              Профессиональный Instagram без доступа к Facebook Business и переноса в другое портфолио.
            </p>
            <Button
              type="button"
              className="w-full"
              disabled={!canStart}
              onClick={() => start.mutate({ authMethod: 'INSTAGRAM_LOGIN' })}
            >
              Подключить через Instagram
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
}
